import { defineStore } from "pinia";
import { withErrorHandling } from "~/helpers/withErrorHandling";
import { withErrorMessage } from "~/helpers/withErrorMessage";
import { withLoading } from "~/helpers/withLoading";
import type {
  KeyValue,
  WebModelPaymentAccount,
} from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import {
  addContributingCompanyToHub,
  createPaymentAccount,
  createPaymentAccountLink,
  findPaymentAccountsByCompany,
  getPaymentConfig,
  refreshPaymentAccountData,
} from "~/model/api/pLCnextStoreFrontendAPI";
import { useCompanyStore } from "./companystore";

interface State {
  companyPaymentAccounts: WebModelPaymentAccount[];
}

export const useDeveloperCompanyStore = defineStore("developerCompanyStore", {
  state: (): State => ({
    companyPaymentAccounts: [], // initial state
  }),
  getters: {
    hasPaymentAccount: (state) => (hubName: string) => {
      return state.companyPaymentAccounts.some(
        (account) => account?.hub?.name === hubName,
      );
    },
  },
  actions: {
    async addDeveloperCompanyToHub(hubName: string) {
      const companyStore = useCompanyStore();
      if (!companyStore.userCompany) {
        console.error("No company found in company store");
      } else {
        const { data } = await withErrorMessage(
          withLoading(
            addContributingCompanyToHub(hubName, companyStore.userCompany.id),
          ),
        );
        console.debug("addDeveloperCompanyToHub: ", data);
        if (data) {
          companyStore.userCompany = data;
        }
      }
    },

    async loadPaymentAccounts() {
      const companyStore = useCompanyStore();
      if (!companyStore.userCompany) {
        console.error("No company found in company store");
      } else {
        const { data, error } = await withErrorHandling(
          withErrorMessage(
            withLoading(
              findPaymentAccountsByCompany({
                "company-id": companyStore.userCompany.id,
              }),
            ),
          ),
        );
        console.debug("loadPaymentAccounts: ", data, error);
        if (data) {
          this.companyPaymentAccounts = data;
        }
      }
      return this.companyPaymentAccounts;
    },
    async createPaymentAccount(hubName: string): Promise<boolean> {
      const companyStore = useCompanyStore();
      if (!companyStore.userCompany) {
        console.error("No company found in company store");
      } else {
        const { data, error } = await withErrorHandling(
          withErrorMessage(
            withLoading(
              createPaymentAccount({
                "company-id": companyStore.userCompany.id,
                "hub-name": hubName,
              }),
            ),
          ),
        );
        console.debug("createPaymentAccount: ", data, error);
        await this.loadPaymentAccounts();
      }
      return true;
    },

    // only stripe (us hub)
    async createPaymentAccountLink(hubName: string): Promise<false | string> {
      const companyStore = useCompanyStore();
      if (!companyStore.userCompany) {
        console.error("No company found in company store");
        return false;
      } else {
        const { data, error } = await withErrorHandling(
          withErrorMessage(
            withLoading(
              createPaymentAccountLink(companyStore.userCompany.id, hubName),
            ),
          ),
        );
        console.debug("createPaymentAccountLink: ", data, error);
        await this.loadPaymentAccounts();

        if (!error && data) {
          return data;
        }
        return false;
      }
    },

    // only stripe (us hub)
    async refreshPaymentAccountDataForStripe(
      hubName: string,
    ): Promise<boolean> {
      const companyStore = useCompanyStore();
      if (!companyStore.userCompany) {
        console.error("No company found in company store");
      } else {
        const { data, error } = await withErrorHandling(
          withErrorMessage(
            withLoading(
              refreshPaymentAccountData(companyStore.userCompany.id, hubName),
            ),
          ),
        );
        console.debug("createPaymentAccountLink: ", data, error);
        await this.loadPaymentAccounts();
      }
      return true;
    },

    async getNovalnetConfigurationData(): Promise<KeyValue[] | undefined> {
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(getPaymentConfig())),
      );
      console.debug("getNovalnetConfigurationData: ", data, error);
      return data;
    },

    clear() {
      this.$reset();
    },
  },
});
